import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import axios from "axios";

import Header from "../../components/Header";
import config from "../../config";
import { getAuthToken } from "../../utils/authStorage";
import { formatStatusLabel, getStatusChipSx } from "./hpcAdminStatusStyles";
import { HPC_COLORS } from "./hpcTheme";
import HpuPaymentPanel from "./HpuPaymentPanel";

const formatLabel = (value) =>
  (value || "")
    .toString()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const authConfig = () => {
  const token = getAuthToken();
  return {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
};

// Fields matched by the search box. NIC is matched when the list includes it.
const SEARCH_FIELDS = ["membership_id", "full_name", "nic_passport", "mobile_number"];
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

// Desktop columns fill the space below the page header and scroll on their own.
const DESKTOP_COLUMN_HEIGHT = "calc(100dvh - 210px)";

const HpuPaymentReceive = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [registrations, setRegistrations] = useState([]);
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const workspaceRef = useRef(null);
  const selectRequestRef = useRef(0);

  const loadRegistrations = async () => {
    setListLoading(true);
    setListError("");
    try {
      const response = await axios.get(`${config.apiBaseUrl}/hpc/admin/registrations`, authConfig());
      setRegistrations(response.data.registrations || []);
    } catch (error) {
      setListError(error.response?.data?.error || "Could not load HPU members.");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadRegistrations();
  }, []);

  const filteredRegistrations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return registrations;
    const queryDigits = onlyDigits(query);
    return registrations.filter((member) =>
      SEARCH_FIELDS.some((field) => {
        const value = member[field];
        if (!value) return false;
        if (String(value).toLowerCase().includes(query)) return true;
        // Phone numbers match with or without spaces and dashes.
        return field === "mobile_number" && queryDigits.length >= 3 && onlyDigits(value).includes(queryDigits);
      }),
    );
  }, [registrations, search]);

  const loadProfile = async (member) => {
    const response = await axios.get(
      `${config.apiBaseUrl}/hpc/admin/registrations/${encodeURIComponent(member.membership_id)}`,
      authConfig(),
    );
    return response.data.registration || null;
  };

  const selectMember = async (member) => {
    if (selectedMember?.membership_id === member.membership_id && profile) return;
    const requestId = selectRequestRef.current + 1;
    selectRequestRef.current = requestId;
    setSelectedMember(member);
    setProfile(null);
    setProfileError("");
    setProfileLoading(true);
    // Only the workspace column moves back to its top; the page itself never scrolls.
    if (workspaceRef.current) workspaceRef.current.scrollTop = 0;
    try {
      const nextProfile = await loadProfile(member);
      if (selectRequestRef.current === requestId) setProfile(nextProfile);
    } catch (error) {
      if (selectRequestRef.current === requestId) {
        setProfileError(error.response?.data?.error || "Could not load HPU member information.");
      }
    } finally {
      if (selectRequestRef.current === requestId) setProfileLoading(false);
    }
  };

  const refreshSelectedMember = async () => {
    if (!selectedMember) return;
    try {
      setProfile(await loadProfile(selectedMember));
    } catch (error) {
      setProfileError(error.response?.data?.error || "Could not refresh HPU member information.");
    }
  };

  const clearSelection = () => {
    selectRequestRef.current += 1;
    setSelectedMember(null);
    setProfile(null);
    setProfileError("");
    setProfileLoading(false);
  };

  const searchPanel = (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isDesktop ? DESKTOP_COLUMN_HEIGHT : "auto",
        minHeight: isDesktop ? 420 : 0,
      }}
    >
      <CardContent sx={{ pb: 1, flexShrink: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight={700}>Find member</Typography>
          <Tooltip title="Refresh list">
            <span>
              <IconButton onClick={loadRegistrations} disabled={listLoading} aria-label="Refresh member list">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name, membership ID, NIC or phone"
          sx={{ mt: 1.5 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
        {!listLoading && !listError && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {filteredRegistrations.length} of {registrations.length} members
          </Typography>
        )}
      </CardContent>

      {listError && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }} action={<Button onClick={loadRegistrations}>Retry</Button>}>
          {listError}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 1,
          pb: 1,
          ...(isDesktop ? {} : { maxHeight: "60vh" }),
        }}
      >
        {listLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : filteredRegistrations.length === 0 ? (
          <Alert severity="info" sx={{ mx: 1 }}>
            {search ? "No HPU members match this search." : "No HPU members are available."}
          </Alert>
        ) : (
          <List dense disablePadding>
            {filteredRegistrations.map((member) => {
              const selected = selectedMember?.membership_id === member.membership_id;
              return (
                <ListItemButton
                  key={member.id || member.membership_id}
                  selected={selected}
                  onClick={() => selectMember(member)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    alignItems: "flex-start",
                    borderLeft: "3px solid transparent",
                    "&.Mui-selected": {
                      borderLeftColor: HPC_COLORS.hpuOrange,
                      backgroundColor: alpha(HPC_COLORS.hpuOrange, 0.16),
                    },
                    "&.Mui-selected:hover": { backgroundColor: alpha(HPC_COLORS.hpuOrange, 0.22) },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" fontWeight={700} noWrap>{member.full_name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {member.membership_id} · {formatLabel(member.membership_type) || "No package"}
                    </Typography>
                  </Box>
                  <Chip
                    label={formatStatusLabel(member.status)}
                    size="small"
                    sx={{ ml: 1, flexShrink: 0, ...getStatusChipSx(member.status) }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Card>
  );

  const workspaceContent = !selectedMember ? (
    <Card sx={{ height: isDesktop ? "100%" : "auto", minHeight: 240 }}>
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1.5,
          py: 6,
        }}
      >
        <PaymentsOutlinedIcon sx={{ fontSize: 48, color: HPC_COLORS.hpuOrange }} />
        <Typography variant="h5" fontWeight={700}>No member selected</Typography>
        <Typography variant="body1" color="text.secondary">
          Search and select a member to receive a payment.
        </Typography>
      </CardContent>
    </Card>
  ) : profileLoading ? (
    <Card><CardContent><Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box></CardContent></Card>
  ) : profileError ? (
    <Alert severity="error" action={<Button onClick={() => selectMember(selectedMember)}>Retry</Button>}>{profileError}</Alert>
  ) : profile ? (
    <HpuPaymentPanel key={profile.membership_id} registration={profile} onPaymentCompleted={refreshSelectedMember} />
  ) : null;

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="HPU Payment Receive" subtitle="TEBA HPU · Receive and review member payments" />

      {isDesktop ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(320px, 35%) minmax(0, 1fr)",
            gap: 2,
            alignItems: "start",
          }}
        >
          {searchPanel}
          <Box
            ref={workspaceRef}
            sx={{
              height: DESKTOP_COLUMN_HEIGHT,
              minHeight: 420,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {workspaceContent}
          </Box>
        </Box>
      ) : selectedMember ? (
        <Box ref={workspaceRef}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={clearSelection} sx={{ mb: 2 }}>
            Back to search
          </Button>
          {workspaceContent}
        </Box>
      ) : (
        searchPanel
      )}
    </Box>
  );
};

export default HpuPaymentReceive;
